import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Loader2, QrCode, RefreshCw, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { IdentityUploadFlow } from "./IdentityUploadFlow";

// Breve "ding" di conferma (Web Audio, nessun file): utile se la finestra è in secondo piano
function playDing(freq = 880) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.65);
  } catch { /* audio non disponibile: silenzioso */ }
}

export function IdentityVerificationCard({ onChanged }) {
  const { t } = useLanguage();
  const [info, setInfo] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  // arrivo da email/banner con ?focus=identity: scorri subito alla card
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("focus") === "identity") {
      document.querySelector('[data-testid="identity-verification-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);
  const closeTimer = useRef(null);

  const load = async () => {
    const { data } = await api.get("/identity/status");
    setInfo({ data: data.data, retention_days: data.retention_days, verified: data.verified,
              blocked: data.blocked, upload_token: data.upload_token || null });
  };

  const start = async (renew = false) => {
    setBusy(true);
    try {
      const started = await api.post("/identity/session", { renew });
      setInfo((prev) => ({ ...prev, ...started.data, error: undefined }));
      // riallinea con lo stato server (deadline, blocco, token) dopo avvio/riavvio
      await load().catch(() => {});
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load().catch(() => setInfo({ error: true })); }, []);
  useEffect(() => {
    if (!qrOpen) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [qrOpen]);

  // Mentre il QR è aperto, intercetta l'invio dal telefono: aggiorna lo stato della
  // sessione ogni 4s (progresso foto e conferma di ricezione) finché resta bozza o da rifare.
  // Alla ricezione: ding di conferma e chiusura automatica del pannello dopo 6s.
  useEffect(() => {
    const current = info?.data?.status || "draft";
    if (!qrOpen || (current !== "draft" && current !== "rejected")) return undefined;
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get("/identity/status");
        // merge: la risposta di /identity/status non include upload_token — preservarlo o il QR sparisce
        setInfo((prev) => ({ ...prev, data: data.data, retention_days: data.retention_days, verified: data.verified }));
        const next = data?.data?.status;
        if (next && next !== current) {
          playDing(880);
          onChanged?.();
          if (next === "submitted") {
            closeTimer.current = setTimeout(() => setQrOpen(false), 6000);
          }
        }
      } catch { /* polling best-effort */ }
    }, 4000);
    return () => clearInterval(poll);
  }, [qrOpen, info?.data?.status]);

  // Esito della review: con pratica "in revisione" controlla ogni 20s l'esito admin
  // (approvata/rifiutata) e aggiorna la card senza ricaricare la pagina
  useEffect(() => {
    const current = info?.data?.status;
    if (current !== "submitted") return undefined;
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get("/identity/status");
        const next = data?.data?.status;
        if (next && next !== "submitted") {
          setInfo((prev) => ({ ...prev, data: data.data, retention_days: data.retention_days, verified: data.verified }));
          playDing(next === "approved" ? 1174 : 392);
          onChanged?.();
        }
      } catch { /* polling best-effort */ }
    }, 20000);
    return () => clearInterval(poll);
  }, [info?.data?.status]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const renew = () => start(true);

  const secondsLeft = useMemo(() => {
    if (!info?.data?.expires_at) return 0;
    return Math.max(0, Math.floor((new Date(info.data.expires_at).getTime() - now) / 1000));
  }, [info?.data?.expires_at, now]);
  const handoffUrl = info?.upload_token ? `${window.location.origin}/identity-upload/${info.upload_token}` : "";

  if (!info) return <div className="rounded-3xl bg-white p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>;
  if (info.error) return null;
  const session = info.data;
  const status = session?.status || "draft";
  const filesDone = session?.files ? Object.values(session.files).filter(Boolean).length : 0;
  const filesTotal = session?.required_kinds?.length || 4;

  return (
    <section data-testid="identity-verification-card" className="rounded-3xl border border-[var(--border-subtle)] bg-white/70 p-6 sm:p-8 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
        <div>
          <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF6B35]">{t("id.eyebrow")}</div>
          <h3 className="mt-2 font-display font-bold text-2xl text-slate-900">{t("id.title")}</h3>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">{t("id.sub")}</p>
          <p className="mt-2 text-xs text-slate-400">{t("id.retention").replace("{days}", info.retention_days || 360)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid="identity-status-badge" className={`rounded-full px-4 py-2 font-mono-t text-[9px] font-bold uppercase tracking-widest ${
            status === "approved" || (info.verified && !session) ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
            status === "submitted" ? "bg-amber-50 text-amber-800 border border-amber-300" :
            status === "rejected" ? "bg-rose-50 text-rose-700 border border-rose-200" :
            status === "expired" ? "bg-slate-200 text-slate-700 border border-slate-300" : "bg-slate-100 text-slate-600"}`}>
            {t(`id.status.${status}`)}
          </span>
          {info.upload_token && (status === "draft" || status === "rejected") && (
            <button type="button" data-testid="kyc-qr-handoff-button" onClick={() => setQrOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#1E293B] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors">
              <QrCode className="w-4 h-4" /> {t("id.continuePhone")}
            </button>
          )}
        </div>
      </div>

      {/* Scadenza rifacimento: oltre questa data la pratica viene azzerata e gli ordini bloccati */}
      {status === "rejected" && session?.redo_deadline_at && (
        <div data-testid="identity-redo-deadline" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {t("id.redoDeadline").replace("{date}", new Date(session.redo_deadline_at).toLocaleDateString())}
        </div>
      )}

      {/* Dati letti dall'AI sul documento e salvati: il cliente vede cosa è stato registrato */}
      {session?.doc_data && (session.doc_data.full_name || session.doc_data.doc_number) && (
        <div data-testid="identity-doc-data" className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3">
          <div className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("id.aiSavedTitle")}</div>
          <div className="mt-1 text-sm text-slate-700">
            {[session.doc_data.full_name, session.doc_data.doc_number, session.doc_data.expiry_date].filter(Boolean).join(" · ")}
          </div>
        </div>
      )}

      {/* Pratica approvata: solo se l'ULTIMA sessione è approvata o non c'è alcuna pratica aperta.
          Una sessione rifiutata (documenti da rifare) non deve mai mostrare "tutto approvato". */}
      {status === "approved" || (info.verified && !session) ? (
        <div data-testid="identity-approved-box" className="mt-6 rounded-3xl bg-emerald-50 border border-emerald-200 p-6 flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
          <div>
            <div className="font-display font-bold text-emerald-800">{t("id.approvedTitle")}</div>
            <p className="text-sm text-emerald-700 mt-1">{t("id.approvedSub")}</p>
          </div>
        </div>
      ) : status === "expired" ? (
        <div data-testid="identity-expired-box" className="mt-6 rounded-3xl border border-rose-200 bg-rose-50 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="font-display font-bold text-rose-800">{t("id.expiredTitle")}</div>
            <p className="text-sm text-rose-700 mt-1">{t("id.expiredBody")}</p>
          </div>
          <button type="button" data-testid="identity-restart-button" onClick={() => start(true)} disabled={busy}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-rose-600 text-white px-6 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-rose-700 transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {t("id.restartBtn")}
          </button>
        </div>
      ) : info.upload_token ? (
        <div className="mt-6">
          <IdentityUploadFlow token={info.upload_token} onSubmitted={() => { load(); onChanged?.(); }} />
        </div>
      ) : status === "submitted" ? (
        <div data-testid="identity-submitted-box" className="mt-6 rounded-3xl bg-amber-50 border border-amber-200 p-6 text-sm text-amber-800">
          {t("id.submittedBox")}
        </div>
      ) : (
        <div className="mt-6 rounded-3xl border border-dashed border-[#0D9488]/30 bg-[#E6F7F5]/40 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-sm text-slate-600 max-w-xl">{t("id.optionalBox")}</p>
          <button type="button" data-testid="self-identification-button" onClick={() => start(false)} disabled={busy}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#0D9488] text-white px-6 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {t("id.start")}
          </button>
        </div>
      )}

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent data-testid="kyc-qr-modal" className="max-w-md p-6">
          <DialogHeader><DialogTitle>{t("id.qrTitle")}</DialogTitle></DialogHeader>
          {status === "draft" || status === "rejected" ? (
            <>
              <p className="text-sm text-slate-500">{t("id.qrSub")}</p>
              <div className="mt-5 rounded-3xl bg-[#1E293B] p-6 text-white flex flex-col items-center">
                {handoffUrl && <QRCodeSVG value={handoffUrl} size={210} bgColor="#1E293B" fgColor="#FFFFFF" />}
                <div data-testid="kyc-qr-session-status" className="mt-4 font-mono-t text-[10px] uppercase tracking-[0.22em] text-slate-300">
                  {t(`id.status.${status}`)}
                </div>
                {filesDone > 0 && (
                  <div data-testid="kyc-qr-progress" className="mt-2 font-mono-t text-[10px] uppercase tracking-[0.22em] text-emerald-300">
                    {t("id.qrProgress").replace("{done}", filesDone).replace("{total}", filesTotal)}
                  </div>
                )}
                <div data-testid="kyc-qr-timer-countdown" className="mt-2 font-mono-t text-xs text-[#FFB08A]">
                  {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, "0")}
                </div>
              </div>
              <button type="button" data-testid="kyc-qr-regenerate-button" onClick={renew} disabled={busy}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {t("id.qrRenew")}
              </button>
            </>
          ) : (
            <div data-testid="kyc-qr-received" className="mt-5 rounded-3xl bg-emerald-50 border border-emerald-200 p-6 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
              <div className="mt-3 font-display font-bold text-lg text-emerald-800">{t("id.qrReceived")}</div>
              <p className="mt-1 text-sm text-emerald-700">{t(status === "approved" ? "id.approvedSub" : "id.qrReceivedSub")}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
